import { MAX_DESCRIPTION_LENGTH, isIsoDate, type IsoDate, type TimeEntryDto } from '@tk/shared';
import { CircleAlert, SquareKanban, TriangleAlert, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { ApiError } from '../lib/api';
import { useKaannos } from '../lib/i18n';
import {
  useCreateEntry,
  useCustomer,
  useProjects,
  useProjectSummary,
  useRecentCombinations,
  useTaskTypes,
  useUpdateEntry,
} from '../lib/queries';
import AsiakasKoodi from './AsiakasKoodi';
import AsiakasValitsin from './AsiakasValitsin';
import Dialogi from './Dialogi';
import KestoValitsin from './KestoValitsin';

/**
 * Esitäyttö taulun tehtävästä (PROJEKTINHALLINTA 16): asiakas, projekti ja
 * otsikko kuvauksena. Kesto jää käyttäjän annettavaksi, kuten aina.
 */
export interface KirjausEsitaytto {
  customerId: string;
  projectId: string | null;
  description: string;
  task: { id: string; title: string };
}

interface Props {
  paiva: IsoDate;
  muokattava?: TimeEntryDto | null;
  esitaytto?: KirjausEsitaytto | null;
  auki: boolean;
  onSulje: () => void;
}

/**
 * Kirjauslomake (suunnitelma 10).
 *
 * Hintaa ei näytetä eikä lähetetä: palvelin ratkaisee sen ketjusta ja
 * jäädyttää riville (5.2, 5.3). Lomake päättää vain laskutettavuuden.
 */
export default function KirjausLomake({ paiva, muokattava, esitaytto, auki, onSulje }: Props) {
  const { t, m } = useKaannos();
  const muokkaus = Boolean(muokattava);

  const [customerId, setCustomerId] = useState<string | null>(null);
  const [taskTypeId, setTaskTypeId] = useState<string | null>(null);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [minutes, setMinutes] = useState(120);
  const [billableOverride, setBillableOverride] = useState<boolean | null>(null);
  const [description, setDescription] = useState('');
  const [tehtava, setTehtava] = useState<{ id: string; title: string } | null>(null);
  /**
   * Päivä on muokattavissa vain muokkaustilassa. Uudessa kirjauksessa se
   * tulee siitä päivästä, jonka kohdalta lomake avattiin — päiväkenttä
   * olisi siinä ylimääräinen tapa tehdä virhe.
   */
  const [entryDate, setEntryDate] = useState<IsoDate>(paiva);
  const [virhe, setVirhe] = useState<ApiError | null>(null);
  const [varoitukset, setVaroitukset] = useState<string[]>([]);

  const { data: customer } = useCustomer(customerId);
  const { data: taskTypes } = useTaskTypes(customerId);
  const { data: projects } = useProjects(customerId);
  const { data: projectSummary } = useProjectSummary(projectId);
  const { data: recent } = useRecentCombinations();

  const luo = useCreateEntry();
  const paivita = useUpdateEntry();
  const tallentaa = luo.isPending || paivita.isPending;

  const taskType = taskTypes?.find((row) => row.id === taskTypeId) ?? null;
  const billable = customer?.isInternal ? false : (billableOverride ?? taskType?.billable ?? true);

  // Lomake nollataan aina avattaessa: edellisen kirjauksen jäänteet
  // väärässä kentässä on juuri se virhe, jota ei huomaa ennen tallennusta.
  useEffect(() => {
    if (!auki) return;
    setVirhe(null);
    setVaroitukset([]);
    setEntryDate(muokattava?.entryDate ?? paiva);
    if (muokattava) {
      setCustomerId(muokattava.customer.id);
      setTaskTypeId(muokattava.taskType.id);
      setProjectId(muokattava.project?.id ?? null);
      setMinutes(muokattava.minutes);
      setBillableOverride(muokattava.billable);
      setDescription(muokattava.description ?? '');
      setTehtava(muokattava.task);
    } else {
      setCustomerId(esitaytto?.customerId ?? null);
      setTaskTypeId(null);
      setProjectId(esitaytto?.projectId ?? null);
      setMinutes(120);
      setBillableOverride(null);
      setDescription(esitaytto?.description.slice(0, MAX_DESCRIPTION_LENGTH) ?? '');
      setTehtava(esitaytto?.task ?? null);
    }
  }, [auki, muokattava, esitaytto, paiva]);

  function valitseAsiakas(id: string | null) {
    setCustomerId(id);
    // Tehtävälaji ja projekti kuuluvat aina asiakkaalle: vanhan valinnan
    // säilyttäminen tuottaisi ristiin valitun kirjauksen (4.3).
    setTaskTypeId(null);
    setProjectId(null);
    setBillableOverride(null);
    // Tehtävä kuuluu asiakkaalle: toisen asiakkaan kirjaus ei voi viitata siihen.
    setTehtava(null);
  }

  async function tallenna(jatka: boolean) {
    setVirhe(null);
    setVaroitukset([]);

    if (!customerId || !taskTypeId) {
      setVirhe(new ApiError(400, 'VALIDATION_FAILED', t('kirjaus.valitseAsiakasJaTehtavalaji')));
      return;
    }

    const payload = {
      entryDate,
      customerId,
      taskTypeId,
      projectId,
      minutes,
      billable,
      description: description.trim() ? description.trim() : null,
      ...(muokattava ? {} : { kanbanTaskId: tehtava?.id ?? null }),
    };

    try {
      const result = muokattava
        ? await paivita.mutateAsync({ id: muokattava.id, input: payload })
        : await luo.mutateAsync(payload);

      if (result.warnings.length > 0) {
        setVaroitukset(result.warnings.map((warning) => warning.message));
      }

      if (jatka && !muokattava) {
        // Sarjakirjaus: asiakas ja tehtävälaji jäävät, kesto ja kuvaus eivät.
        setMinutes(120);
        setDescription('');
        if (result.warnings.length === 0) setVaroitukset([t('kirjaus.tallennettu')]);
        return;
      }

      onSulje();
    } catch (error) {
      setVirhe(error instanceof ApiError ? error : null);
    }
  }

  return (
    <Dialogi
      otsikko={`${muokkaus ? t('kirjaus.muokkaa') : t('kirjaus.uusi')} · ${m.pvm(entryDate)}`}
      auki={auki}
      onSulje={onSulje}
      leveys="max-w-xl"
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void tallenna(false);
        }}
        className="space-y-4"
      >
        {!muokkaus && recent && recent.length > 0 && (
          <div>
            <p className="text-teksti-vaimea mb-1.5 text-xs font-medium">
              {t('kirjaus.viimeksiKaytetyt')}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {recent.map((combination) => (
                <button
                  key={`${combination.customerId}-${combination.taskTypeId}-${combination.projectId ?? ''}`}
                  type="button"
                  onClick={() => {
                    setCustomerId(combination.customerId);
                    setTaskTypeId(combination.taskTypeId);
                    setProjectId(combination.projectId);
                    setBillableOverride(null);
                  }}
                  className="border-reuna hover:border-korostus hover:bg-korostus-pehmea rounded-md border px-2.5 py-1.5 text-left text-xs transition-colors"
                >
                  <span className="text-teksti flex items-center gap-1 font-medium">
                    <AsiakasKoodi
                      code={combination.customerCode}
                      colorKey={combination.customerColorKey}
                    />
                    {combination.taskTypeName}
                  </span>
                  {combination.projectName && (
                    <span className="text-teksti-vaimea block">{combination.projectName}</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Siirto toiseen päivään. Viikkonäkymässä saman voi tehdä
          raahaamalla, mutta raahaus ei saa olla ainoa keino: se ei toimi
          näppäimistöllä (15, WCAG 2.1 AA). Palvelin vaatii molempien
          kuukausien olevan auki (6.2) ja kertoo sen virheessä. */}
        {muokkaus && (
          <label className="block">
            <span className="text-teksti mb-1 block text-sm font-medium">{t('kirjaus.paiva')}</span>
            <input
              type="date"
              value={entryDate}
              onChange={(event) => {
                const arvo = event.target.value;
                if (isIsoDate(arvo)) setEntryDate(arvo);
              }}
              className="border-reuna bg-pinta text-teksti w-full rounded-md border px-3 py-2 text-sm sm:max-w-48"
            />
          </label>
        )}

        <label className="block">
          <span className="text-teksti mb-1 block text-sm font-medium">{t('yleinen.asiakas')}</span>
          <AsiakasValitsin
            valittu={customer ?? null}
            onValitse={(next) => valitseAsiakas(next?.id ?? null)}
            autoFocus={!muokkaus}
          />
        </label>

        <label className="block">
          <span className="text-teksti mb-1 block text-sm font-medium">
            {t('kirjaus.tehtavalaji')}
          </span>
          <select
            value={taskTypeId ?? ''}
            disabled={!customerId}
            onChange={(event) => {
              setTaskTypeId(event.target.value || null);
              setBillableOverride(null);
            }}
            className="border-reuna bg-pinta text-teksti w-full rounded-md border px-3 py-2 text-sm disabled:opacity-50"
          >
            <option value="">
              {customerId ? t('yleinen.valitse') : t('kirjaus.valitseEnsinAsiakas')}
            </option>
            {taskTypes?.map((row) => (
              <option key={row.id} value={row.id}>
                {row.name}
                {!row.billable ? t('kirjaus.eiLaskuteta') : ''}
              </option>
            ))}
          </select>
        </label>

        {projects && projects.length > 0 && (
          <label className="block">
            <span className="text-teksti mb-1 block text-sm font-medium">
              {t('kirjaus.projekti')}{' '}
              <span className="text-teksti-vaimea font-normal">{t('yleinen.valinnainen')}</span>
            </span>
            <select
              value={projectId ?? ''}
              onChange={(event) => setProjectId(event.target.value || null)}
              className="border-reuna bg-pinta text-teksti w-full rounded-md border px-3 py-2 text-sm"
            >
              <option value="">{t('kirjaus.eiProjektia')}</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>

            {projectSummary?.hours.budget && <ProjektinBudjetti summary={projectSummary} />}
          </label>
        )}

        <div>
          <span className="text-teksti mb-1 block text-sm font-medium">{t('kirjaus.kesto')}</span>
          <KestoValitsin minutes={minutes} onChange={setMinutes} />
        </div>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={billable}
            disabled={customer?.isInternal ?? false}
            onChange={(event) => setBillableOverride(event.target.checked)}
            className="accent-brandi size-4"
          />
          <span className="text-teksti text-sm">
            {t('yleinen.laskutettava')}
            {customer?.isInternal && (
              <span className="text-teksti-vaimea">{t('kirjaus.sisainenEiLaskuteta')}</span>
            )}
          </span>
        </label>

        {tehtava && (
          <p className="text-teksti-vaimea flex items-center gap-1.5 text-sm">
            <SquareKanban className="size-4 shrink-0" aria-hidden="true" />
            <span>{t('kirjaus.tehtava')}</span>
            <Link
              to={`/taulut/tehtava/${tehtava.id}`}
              className="text-korostus truncate hover:underline"
            >
              {tehtava.title}
            </Link>
            {!muokkaus && (
              <button
                type="button"
                onClick={() => setTehtava(null)}
                title={t('kirjaus.poistaTehtavaviite')}
                aria-label={t('kirjaus.poistaTehtavaviite')}
                className="text-teksti-vaimea hover:text-yli ml-auto rounded p-1"
              >
                <X className="size-3.5" aria-hidden="true" />
              </button>
            )}
          </p>
        )}

        <label className="block">
          <span className="text-teksti mb-1 block text-sm font-medium">
            {t('kirjaus.kuvaus')}{' '}
            <span className="text-teksti-vaimea font-normal">{t('yleinen.valinnainen')}</span>
          </span>
          <input
            type="text"
            value={description}
            maxLength={MAX_DESCRIPTION_LENGTH}
            onChange={(event) => setDescription(event.target.value)}
            className="border-reuna bg-pinta text-teksti w-full rounded-md border px-3 py-2 text-sm"
          />
        </label>

        {virhe && (
          <p
            role="alert"
            className="border-yli/40 bg-yli/10 text-teksti flex items-start gap-2 rounded-md border px-3 py-2 text-sm"
          >
            <CircleAlert className="text-yli mt-0.5 size-4 shrink-0" aria-hidden="true" />
            {virhe.message}
          </p>
        )}

        {varoitukset.map((viesti) => (
          <p
            key={viesti}
            role="status"
            className="border-varoitus/40 bg-varoitus/10 text-teksti flex items-start gap-2 rounded-md border px-3 py-2 text-sm"
          >
            <TriangleAlert className="text-varoitus mt-0.5 size-4 shrink-0" aria-hidden="true" />
            {viesti}
          </p>
        ))}

        <div className="flex flex-wrap justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onSulje}
            className="border-reuna text-teksti-vaimea hover:text-teksti rounded-md border px-4 py-2 text-sm"
          >
            {t('yleinen.peruuta')}
          </button>
          {!muokkaus && (
            <button
              type="button"
              disabled={tallentaa}
              onClick={() => void tallenna(true)}
              className="border-reuna text-teksti hover:border-reuna-vahva rounded-md border px-4 py-2 text-sm disabled:opacity-50"
            >
              {t('kirjaus.tallennaJaJatka')}
            </button>
          )}
          <button
            type="submit"
            disabled={tallentaa}
            className="bg-brandi hover:bg-brandi-syva rounded-md px-4 py-2 text-sm font-medium text-white transition-colors disabled:opacity-50"
          >
            {tallentaa ? t('yleinen.tallennetaan') : t('yleinen.tallenna')}
          </button>
        </div>
      </form>
    </Dialogi>
  );
}

/**
 * Projektin tuntibudjetin kuluma kirjaushetkellä (suunnitelma 10).
 *
 * Vain tunnit: eurobudjetti kuuluu hallintaan ja raportteihin, eikä
 * kirjaajan tarvitse nähdä sitä kestoa valitessaan. Palvelin ei lähetä
 * `value`-osaa ilman oikeutta (2.3), mutta tässä sitä ei näytetä
 * kenellekään.
 */
function ProjektinBudjetti({
  summary,
}: {
  summary: NonNullable<ReturnType<typeof useProjectSummary>['data']>;
}) {
  const { t, m } = useKaannos();
  const osuus = summary.hours.usedShare ?? 0;
  const yli = osuus > 1;

  return (
    <div className="text-teksti-vaimea mt-2 space-y-1 text-xs">
      <div className="flex flex-wrap items-center gap-x-2">
        <span>
          {t('kirjaus.budjetti', m.tunnit(Number(summary.hours.budget) * 60))}
          {summary.hours.budgetDays && t('kirjaus.budjettiHtp', m.htp(summary.hours.budgetDays))}
        </span>
        <span className="luvut text-teksti">
          {t('kirjaus.kaytetty', m.tunnit(Number(summary.hours.used) * 60), m.prosentti(osuus, 0))}
        </span>
      </div>
      <div className="bg-reuna h-1.5 w-full overflow-hidden rounded-full">
        <div
          className={yli ? 'bg-yli h-full' : 'bg-teksti-vaimea h-full'}
          style={{ width: `${Math.min(100, osuus * 100)}%` }}
        />
      </div>
    </div>
  );
}
